/**
 * @module services/fullscreen
 *
 * Cross-browser native-fullscreen enter/exit for the video players. Extracted
 * so both the playlist player (MediaPlayer.vue) and the standalone quick-play
 * dialog (EpisodePlayerDialog.vue) share one implementation instead of forking
 * the (subtle, prefix-laden) logic.
 *
 * Two fullscreen targets, chosen by device:
 *   - Desktop: fullscreen the WRAPPER element, so our own custom controls stay
 *     visible over the video.
 *   - Mobile (esp. iOS Safari): fullscreen the VIDEO element itself — iOS only
 *     supports fullscreen on a <video>, and it shows the browser's native
 *     controls in that mode.
 */

/** Whether the document is currently in native fullscreen (standard or webkit). */
export const isFullscreenActive = () =>
  !!(document.fullscreenElement || document.webkitFullscreenElement);

/**
 * Toggle native fullscreen: exits if already fullscreen, otherwise enters —
 * targeting the video element on mobile and the wrapper element on desktop.
 *
 * @param {Object} opts
 * @param {HTMLVideoElement} opts.videoEl - the <video> element (mobile target)
 * @param {HTMLElement} opts.wrapperEl - the player wrapper (desktop target)
 * @param {boolean} opts.isMobile - true on small/touch viewports
 */
export const toggleFullscreen = ({ videoEl, wrapperEl, isMobile }) => {
  // Exit if already in fullscreen (works cross-browser).
  const fsElement =
    document.fullscreenElement || document.webkitFullscreenElement;
  if (fsElement) {
    (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    return;
  }

  if (isMobile && videoEl) {
    // Mobile: fullscreen the video element directly (iOS Safari requirement).
    // This shows the browser's native video controls in fullscreen.
    if (videoEl.requestFullscreen) {
      videoEl.requestFullscreen();
    } else if (videoEl.webkitEnterFullscreen) {
      // iOS Safari specific API for video fullscreen.
      videoEl.webkitEnterFullscreen();
    }
  } else if (wrapperEl) {
    // Desktop: fullscreen the wrapper so our custom controls stay visible.
    if (wrapperEl.requestFullscreen) {
      wrapperEl.requestFullscreen();
    } else if (wrapperEl.webkitRequestFullscreen) {
      wrapperEl.webkitRequestFullscreen();
    }
  }
};
